/**
 * features/alerts/alertStore.ts
 *
 * Centralized alert state. Survives re-renders via module-level singleton.
 * Persists to localStorage so alerts survive page refresh.
 * Deduplicates via cooldown keys so the same event doesn't spam.
 */

export type AlertSeverity = 'info' | 'warning' | 'actionable' | 'invalidation';

export type AlertCategory =
  | 'liquidity_zone'
  | 'sweep'
  | 'structure'      // BOS / CHOCH
  | 'vwap'
  | 'setup'
  | 'thesis'
  | 'opportunity'
  | 'feed'           // data connection
  | 'ai';            // AI status

export interface Alert {
  id:         string;
  symbol:     string | null;   // null = global
  category:   AlertCategory;
  severity:   AlertSeverity;
  title:      string;
  message:    string;
  timestamp:  number;
  read:       boolean;
  dedupeKey:  string;          // prevents duplicate alerts within cooldown window
}

// ─── Store ────────────────────────────────────────────────────────────────────

const STORAGE_KEY    = 'mer_alerts_v1';
const MAX_ALERTS     = 200;
const DEDUPE_COOLDOWN_MS: Record<AlertSeverity, number> = {
  info:         60_000,    // 1 min
  warning:      30_000,
  actionable:   15_000,
  invalidation: 10_000,
};

let alerts: Alert[]              = [];
let dedupeCache = new Map<string, number>(); // dedupeKey -> lastEmitted ts

const listeners = new Set<() => void>();

function notify() { listeners.forEach(fn => fn()); }

// ─── Load from storage ────────────────────────────────────────────────────────

function load() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw) {
      const parsed = JSON.parse(raw) as Alert[];
      // Only keep alerts from the last 4 hours
      const cutoff = Date.now() - 4 * 60 * 60 * 1000;
      alerts = parsed.filter(a => a.timestamp > cutoff);
    }
  } catch {}
}

function save() {
  try { localStorage.setItem(STORAGE_KEY, JSON.stringify(alerts.slice(0, MAX_ALERTS))); }
  catch {}
}

load();

// ─── Subscribe ────────────────────────────────────────────────────────────────

export function subscribeAlerts(fn: () => void): () => void {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

// ─── Emit ─────────────────────────────────────────────────────────────────────

export function emitAlert(
  symbol:   string | null,
  category: AlertCategory,
  severity: AlertSeverity,
  title:    string,
  message:  string,
  dedupeKey?: string
): Alert | null {
  const key = dedupeKey || `${symbol}:${category}:${title}`;
  const lastEmit = dedupeCache.get(key);
  const cooldown = DEDUPE_COOLDOWN_MS[severity];

  if (lastEmit && (Date.now() - lastEmit) < cooldown) {
    return null; // suppressed — same event too recently
  }

  dedupeCache.set(key, Date.now());

  const alert: Alert = {
    id:        `${Date.now()}-${Math.random().toString(36).slice(2,7)}`,
    symbol,
    category,
    severity,
    title,
    message,
    timestamp: Date.now(),
    read:      false,
    dedupeKey: key,
  };

  alerts.unshift(alert);
  if (alerts.length > MAX_ALERTS) alerts = alerts.slice(0, MAX_ALERTS);
  save();
  notify();
  return alert;
}

// ─── Read ─────────────────────────────────────────────────────────────────────

export function getAlerts(options?: {
  symbol?:   string | null;
  severity?: AlertSeverity | AlertSeverity[];
  category?: AlertCategory | AlertCategory[];
  unreadOnly?: boolean;
  limit?:    number;
}): Alert[] {
  let result = [...alerts];

  if (options?.symbol !== undefined) {
    result = options.symbol === null
      ? result.filter(a => a.symbol === null)
      : result.filter(a => a.symbol === options.symbol || a.symbol === null);
  }

  if (options?.severity) {
    const sevs = Array.isArray(options.severity) ? options.severity : [options.severity];
    result = result.filter(a => sevs.includes(a.severity));
  }

  if (options?.category) {
    const cats = Array.isArray(options.category) ? options.category : [options.category];
    result = result.filter(a => cats.includes(a.category));
  }

  if (options?.unreadOnly) result = result.filter(a => !a.read);

  return options?.limit ? result.slice(0, options.limit) : result;
}

export function getUnreadCount(symbol?: string): number {
  return alerts.filter(a =>
    !a.read && (symbol === undefined || a.symbol === symbol || a.symbol === null)
  ).length;
}

export function markRead(id: string) {
  const a = alerts.find(a => a.id === id);
  if (a) { a.read = true; save(); notify(); }
}

export function markAllRead(symbol?: string) {
  alerts.forEach(a => {
    if (!symbol || a.symbol === symbol || a.symbol === null) a.read = true;
  });
  save();
  notify();
}

export function clearAlerts(symbol?: string) {
  alerts = symbol ? alerts.filter(a => a.symbol !== symbol) : [];
  save();
  notify();
}
